import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Result, Spin, Timeline } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import Logo from './Logo';
import ExpressTrackingTimeline from './orders/ExpressTrackingTimeline';
import './OrderTracking.css';

// Create a custom axios instance without auth interceptors for public endpoints
const publicAxios = axios.create({
  baseURL: '', // Use same base URL
  timeout: 10000,
});

// Clear any existing interceptors and prevent inheritance from global axios
publicAxios.interceptors.request.handlers = [];
publicAxios.interceptors.response.handlers = [];

// Override the default headers to ensure no auth headers are sent
publicAxios.defaults.headers.common = {};
publicAxios.defaults.headers.get = {};
publicAxios.defaults.headers.post = {};
publicAxios.defaults.headers.put = {};
publicAxios.defaults.headers.patch = {};
publicAxios.defaults.headers.delete = {};

// Add a request interceptor to ensure no Authorization header is sent
publicAxios.interceptors.request.use(
  (config) => {
    // Remove any Authorization header that might have been added
    delete config.headers.Authorization;
    delete config.headers.authorization;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor that doesn't handle 401s like the global one
publicAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't try to refresh tokens or redirect on 401 for public endpoints
    return Promise.reject(error);
  }
);

const PublicOrderTracking = () => {
  const [searchText, setSearchText] = useState('');
  const [orderStatus, setOrderStatus] = useState(null);
  const [deliveryTrajectory, setDeliveryTrajectory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!searchText.trim()) {
      return;
    }

    setLoading(true);
    setError(null);
    setOrderStatus(null);

    try {
      const response = await publicAxios.get(`/api/orders/public-track/?search=${searchText}`);
      setOrderStatus(response.data);
      
      // Check if order is delivered and has tracking data
      if (response.data.status === 'DELIVERED' && response.data.sf_tracking_data && response.data.sf_tracking_data.length > 0) {
        // Get the last tracking entry
        const trackingData = response.data.sf_tracking_data;
        const lastEntry = trackingData[trackingData.length - 1];
        setDeliveryTrajectory(lastEntry);
      } else {
        setDeliveryTrajectory(null);
      }
    } catch (error) {
      console.error('Error searching order:', error);
      if (error.response?.status === 404) {
        setError('未找到订单，请确保输入信息正确');
      } else {
        setError('搜索订单时出错');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SHIPPING':
        return '#1890ff';
      case 'ARRIVED':
        return '#52c41a';
      case 'INSPECTION_NEEDED':
        return '#faad14';
      case 'INSPECTED':
        return '#722ed1';
      case 'DELIVERING':
        return '#13c2c2';
      case 'DELIVERED':
        return '#52c41a';
      case 'RELEASED':
        return '#52c41a';
      default:
        return '#000000';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'CREATED': '已创建',
      'PRINTED': '已打印',
      'DUPLICATE': '重复订单',
      'SHIPPING': '运输中',
      'PENDING_REVIEW': '未审单',
      'PENDING_CUSTOMS': '待清关',
      'PICKED_UP': '已提货未过机',
      'INSPECTED_PENDING': '已出查验未放行',
      'RELEASED': '已放行',
      'DELIVERING': '派送中',
      'DELIVERED': '已送达',
    };
    return statusMap[status] || status;
  };

  return (
    <div className="order-tracking-container">
      <div className="tracking-header">
        <Logo />
      </div>
      <Card className="tracking-card">
        <div className="search-section">
          <div className="input-group">
            <Input
              placeholder="请输入快递单号"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
              className="tracking-input full-width"
              size="large"
            />
          </div>
          <Button type="primary" onClick={handleSearch} size="large">
            查询
          </Button>
        </div>

        <div className="result-section">
          {loading ? (
            <div className="loading-container">
              <Spin size="large" />
            </div>
          ) : error ? (
            <Result
              status="warning"
              title={error}
            />
          ) : orderStatus ? (
            <div className="status-container">
              <div className="status-header">
                <h2>订单状态</h2>
                <h2>运输轨迹</h2>
              </div>
              <div className="status-content">
                <div className="status-info">
                  <div className="status-item">
                    <span className="label">订单号:</span>
                    <span className="value">{orderStatus.order_number}</span>
                  </div>
                  <div className="status-item">
                    <span className="label">状态:</span>
                    <span 
                      className="value status-value"
                      style={{ color: getStatusColor(orderStatus.status) }}
                    >
                      {getStatusText(orderStatus.status)}
                    </span>
                  </div>
                  {orderStatus.express_number && (
                    <div className="status-item">
                      <span className="label">快递单号:</span>
                      <span className="value">{orderStatus.express_number}</span>
                    </div>
                  )}
                </div>
                
                {/* Tracking Timeline */}
                <div className="timeline-container">
                  {orderStatus.express_number && (
                    <ExpressTrackingTimeline
                      expressNumber={orderStatus.express_number}
                      showOverseasOnly={true}
                      isDelivered={orderStatus.status === 'DELIVERED'}
                      deliveryTrajectory={deliveryTrajectory}
                      orderStatus={orderStatus}
                      onTrackingUpdate={(updatedData) => {
                        if (updatedData?.status) {
                          setOrderStatus(prev => ({
                            ...prev,
                            status: updatedData.status
                          }));
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
};

export default PublicOrderTracking;
